import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaSpotify, FaTiktok } from 'react-icons/fa';
import { SiLinktree } from "react-icons/si";

const Footer = () => {
  useEffect(() => {
    // Set copyright year
    const yearElement = document.getElementById('footer-copyright-year');
    if (yearElement) {
      yearElement.textContent = new Date().getFullYear();
    }
  }, []);

  // Ensure footer animation runs even if WOW.js didn't trigger (SPA routing / screen-size cases)
  useEffect(() => {
    const el = document.querySelector('.ul-footer-middle-wrapper');
    if (!el) return;

    // If animation already applied by WOW, do nothing
    if (el.classList.contains('animate__animated')) return;

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          el.classList.add('animate__animated');
          // leave animate__fadeInUp as-is (already present)
          obs.unobserve(el);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -80px 0px' });

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return (
    <footer className="ul-footer">
      <div className="ul-footer-top">
        <div className="ul-footer-container">
          <div className="ul-footer-top-contact-infos">
            <div className="ul-footer-top-contact-info">
              <div className="ul-footer-top-contact-info-icon">
                <div className="ul-footer-top-contact-info-icon-inner">
                  <i className="flaticon-pin"></i>
                </div>
              </div>
              <div className="ul-footer-top-contact-info-txt">
                <span className="ul-footer-top-contact-info-label">Address</span>
                <h5 className="ul-footer-top-contact-info-address">
                  New York City, NY
                </h5>
              </div>
            </div>

            <div className="ul-footer-top-contact-info">
              <div className="ul-footer-top-contact-info-icon">
                <div className="ul-footer-top-contact-info-icon-inner">
                  <i className="flaticon-email"></i>
                </div>
              </div>
              <div className="ul-footer-top-contact-info-txt">
                <span className="ul-footer-top-contact-info-label">Send Email</span>
                <h5 className="ul-footer-top-contact-info-address">
                  <a href="mailto:pathio.org@gmail.com">pathio.org@gmail.com</a>
                </h5>
              </div>
            </div>

            <div className="ul-footer-top-contact-info">
              <div className="ul-footer-top-contact-info-icon">
                <div className="ul-footer-top-contact-info-icon-inner">
                  <i className="flaticon-love"></i>
                </div>
              </div>
              <div className="ul-footer-top-contact-info-txt">
                <span className="ul-footer-top-contact-info-label">Explore</span>
                <h5 className="ul-footer-top-contact-info-address">
                  <Link to="/services">Our Programs</Link>
                </h5>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="ul-footer-middle">
        <div className="ul-footer-container">
          <div className="ul-footer-middle-wrapper wow animate__fadeInUp">
            <div className="ul-footer-about">
              <Link to="/">
                <img src="/assets/logo_new.png" alt="logo" className="logo" />
              </Link>
              <p className="ul-footer-about-txt">
                Pathio is a student-led 501(c)(3) nonprofit providing peer-to-peer mentorship for students exploring healthcare careers, with a focus on supporting Central Asian communities. We also raise awareness around general health topics and connect individuals to trusted local resources.
              </p>
              <div className="ul-footer-socials">
                    <a href="https://www.instagram.com/pathio_org?igsh=Z2cxOGJlbWR4ZGdn" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><i className="flaticon-instagram"></i></a>
                    <a href="https://www.tiktok.com/@pathio_org?_r=1&_t=ZP-93lKmnOrnJ8" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                    <FaTiktok />
                    </a>
                    <a href="https://open.spotify.com/user/31wi6czixyazxhzyardx4bcp3cui?si=4c2108a075244cea" target="_blank" rel="noopener noreferrer" aria-label="Spotify"><FaSpotify /></a>
                    <a href="https://youtube.com/@pathio_org?si=52TSlqZThY2-Gail" target="_blank" rel="noopener noreferrer" aria-label="YouTube"><i className="flaticon-youtube"></i></a>
                    <a href="https://linkedin.com/company/pathio-org/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><i className="flaticon-linkedin-big-logo"></i></a>
                    <a href="https://linktr.ee/pathio.org" target="_blank" rel="noopener noreferrer" aria-label="Linktree"><SiLinktree /></a>
              </div>
            </div>

            <div className="ul-footer-widget">
              <h3 className="ul-footer-widget-title">Quick Links</h3>
              <div className="ul-footer-widget-links">
                <Link to="/">Home</Link>
                <Link to="/about">About</Link>
                <Link to="/team">Team</Link>
                <Link to="/blog">Pathway Guides</Link>
                <Link to="/pathio-buddies">Pathio Buddies</Link>
              </div>
            </div>

            <div className="ul-footer-widget ul-footer-recent-posts">
              <h3 className="ul-footer-widget-title">Pathway Guides</h3>
              <div className="ul-blog-sidebar-posts">
                <div className="ul-blog-sidebar-post ul-footer-post">
                  <div className="img">
                    <img
                      src="/assets/new-img/a-girl-writing-with-a-pen-on-a-board-of-equations-2025-04-04-01-07-31-utc.jpeg"
                      alt="Pre-Medicine Guide"
                    />
                  </div>
                  <div className="txt">
                    <span className="date">
                      <span className="icon"><i className="flaticon-calendar"></i></span>
                      <span>Pathway Guide</span>
                    </span>
                    <h4 className="title">
                      <Link to="/blog#pre-med">Pre-Medicine: A Student Guide</Link>
                    </h4>
                  </div>
                </div>

                <div className="ul-blog-sidebar-post ul-footer-post">
                  <div className="img">
                    <img
                      src="/assets/new-img/black-young-adult-woman-teaching-caucasian-girl-ab-2025-12-06-13-19-59-utc.jpg"
                      alt="Pre-RN Guide"
                    />
                  </div>
                  <div className="txt">
                    <span className="date">
                      <span className="icon"><i className="flaticon-calendar"></i></span>
                      <span>Pathway Guide</span>
                    </span>
                    <h4 className="title">
                      <Link to="/blog#pre-rn">Pre-RN: A Student Guide</Link>
                    </h4>
                  </div>
                </div>
              </div>
            </div>

            <div className="ul-footer-widget ul-nwsltr-widget">
              <h3 className="ul-footer-widget-title">Contact Us</h3>
              <div className="ul-footer-widget-links ul-footer-contact-links">
                <a href="mailto:pathio.org@gmail.com">
                  <i className="flaticon-mail"></i> pathio.org@gmail.com
                </a>
                <Link to="/contact">
                  <i className="flaticon-right"></i> Get Support
                </Link>
              </div>
              <form action="#" className="ul-nwsltr-form">
                <div className="top">
                  <input
                    type="email"
                    name="email"
                    id="nwsltr-email"
                    placeholder="Your Email Address"
                    className="ul-nwsltr-input"
                  />
                  <button type="submit"><i className="flaticon-next"></i></button>
                </div>
                <div className="agreement">
                  <label htmlFor="nwsltr-agreement" className="ul-checkbox-wrapper">
                    <input
                      type="checkbox"
                      name="agreement"
                      id="nwsltr-agreement"
                      hidden
                    />
                    <span className="ul-checkbox"><i className="flaticon-tick"></i></span>
                    <span className="ul-checkbox-txt">I agree with the <Link to="/privacy">Privacy Policy</Link></span>
                  </label>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className="ul-footer-bottom">
        <div className="ul-footer-container">
          <div className="ul-footer-bottom-wrapper">
            <p className="copyright-txt">
              &copy; <span id="footer-copyright-year"></span> Pathio. All rights reserved
            </p>
            <div className="ul-footer-bottom-nav">
              <Link to="/terms">Terms & Conditions</Link> <Link to="/privacy">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </div>

      <div className="ul-footer-vectors">
        <img
          src="/assets/img/footer-vector-img.png"
          alt="Footer Image"
          className="ul-footer-vector-1"
        />
      </div>
    </footer>
  );
};

export default Footer;

